import _, {
  indexOf,
  isNumber,
  isObject,
  isString,
  keys,
  reduce,
  values,
  get,
  isArray,
  compact,
  isFunction,
  toUpper,
} from 'lodash';
import {
  planTypes,
  BILLING_CYCLE,
  BASE_PLAN_SUB_TYPES,
  BillingCycleEnum,
  getPlanTypeFromParentCategory,
  MULTI_UPDATE_PLAN_TYPES,
  STAND_ALONE_PLAN_TYPES,
  NEW_PRO_CATEGORY,
  AUDIO_SUB_TYPES, ZONE_PRO_PLUS_PARENT_CATEGORY, SMB_ALWAYS_FETCH_NONSTANDARD_TYPES, NEW_PRO_SPECIAL_CYCLES,
} from '@/common/constants';
import { flowTypes, pageConfig } from '@/common/service/page-config';
import { getPlanTypeByCategory } from '@/common/service/parse';
import { getBasePlanInfo } from '@/common/product';
import { cmrCapacityMap } from '@/plans/CMR/pre';
import {
  EVENT_SAAS_ADDITIONAL_CO_EDITOR,
  EVENT_SAAS_ADDITIONAL_HOST, EVENT_SAAS_CORE_ADDITIONAL_CO_EDITOR,
  EVENT_SAAS_CORE_ADDITIONAL_HOST,
} from '@/common/constants';
import { isDebug } from '@/utils';
import { zcmStorageCapacityMap } from '@/plans/ZCM_STORAGE/pre';

const ZoomOneProPlusKey = window?.newBillingConfig?.enableNewProPlus ? 'zoom-workplace-pro-plus' : 'zoom-one-pro-plus';

export const ONLINE_NOT_SUPPORT_BILLING_CYCLE = [
  BILLING_CYCLE.QUARTERLY,
  BILLING_CYCLE.TWO_YEARS,
  BILLING_CYCLE.THREE_YEARS,
  BillingCycleEnum[BILLING_CYCLE.QUARTERLY],
  BillingCycleEnum[BILLING_CYCLE.TWO_YEARS],
  BillingCycleEnum[BILLING_CYCLE.THREE_YEARS]
];
export const isOnlineNotSupportPlan = ({ planType, billingCycle, category }) => {
  const specialPlanMap = {
    [planTypes.CMR]: () => [...ONLINE_NOT_SUPPORT_BILLING_CYCLE, BILLING_CYCLE.ANNUAL, BillingCycleEnum[BILLING_CYCLE.ANNUAL]].includes(billingCycle),
    [planTypes.TOLLFREE]: () => {
      if (category && category === AUDIO_SUB_TYPES.TOLL_FREE) {
        // Audio One Year Prepay Monthly Usage is not support in online
       return [...ONLINE_NOT_SUPPORT_BILLING_CYCLE, BILLING_CYCLE.ANNUAL, BillingCycleEnum[BILLING_CYCLE.ANNUAL]].includes(billingCycle)
      } else {
        return billingCycle && ONLINE_NOT_SUPPORT_BILLING_CYCLE.includes(billingCycle);
      }
    },
  }
  if (isFunction(specialPlanMap[planType])) {
    return specialPlanMap[planType]()
  }
  return billingCycle && ONLINE_NOT_SUPPORT_BILLING_CYCLE.includes(billingCycle);
}

// Meeting base plans are allowed to switch billing cycle in edit flow.
// Other add-on plans are NOT allowed to switch.
const SMB_MEETING_BASE_PLAN = [planTypes.ZOOM_MEETING, planTypes.ZOOM_ONE_BUNDLE, planTypes.ZONE_PRO_PLUS];

//todo will support in feture release, and think support zoom one add2card flow
export const SMB_SUPPORT_ADD2CARD = false;

function convertBillingCycleEnumToString(billingCycleNumer) {
  if (isNumber(billingCycleNumer) && BillingCycleEnum.has(billingCycleNumer)) {
    return BillingCycleEnum[billingCycleNumer];
  } else {
    return null;
  }
}

const BILLING_CYCLE_PREFIX = {
  [BILLING_CYCLE.ONE_MONTH]: 'one_month',
  [BILLING_CYCLE.MONTHLY]: 'month',
  [BILLING_CYCLE.QUARTERLY]: 'quarterly',
  [BILLING_CYCLE.ANNUAL]: 'yearly',
  [BILLING_CYCLE.TWO_YEARS]: 'two_years',
  [BILLING_CYCLE.THREE_YEARS]: 'three_years',
  [BILLING_CYCLE.ANNUAL_TERM_MONTHLY]: 'annual_term_monthly',
};

const PRICING_INFO_FIELD_SUFFIX = {
  PLAN_CODE: 'plancode',
  PLAN_TYPE: 'plantype',
  AMOUNT: 'amount',
  HIDE: 'hide',
  DISABLE: 'disable',
};

export function getPricingFieldName(billingCycle, suffix) {
  const prefix = BILLING_CYCLE_PREFIX[billingCycle];
  console.assert(prefix, 'fail to match billing cycle prefix');
  return [prefix, suffix].join('_');
}

export const SMB_BILLING_CYCLE_MAP = reduce(
  values(BILLING_CYCLE),
  (obj, billingCycle) => {
    obj[billingCycle] = {
      isOnline: !ONLINE_NOT_SUPPORT_BILLING_CYCLE.includes(billingCycle),
      index: indexOf(values(BILLING_CYCLE), billingCycle),
      prefix: BILLING_CYCLE_PREFIX[billingCycle] || '',
    };
    return obj;
  },
  {},
);

const isBizPlusContext = () => pageConfig?.editConfig?.planType === planTypes.ZOOM_ONE_BUNDLE || getPlanTypeByCategory?.(pageConfig?.plan) === planTypes.ZOOM_ONE_BUNDLE;
const isProPlusContext = () => pageConfig?.editConfig?.planType === planTypes.ZONE_PRO_PLUS || getPlanTypeByCategory?.(pageConfig?.plan) === planTypes.ZONE_PRO_PLUS;
const isPbxContext = () => pageConfig?.editConfig?.planType === planTypes.ZOOM_PHONE;
/**
 *
 * @param {List<Plan>} subscriptions
 * @param {string} plancode
 * @description Check edit plan has unsupport billing cycle or not
 * @returns {boolean}
 */
export function hasUnSupportCyclePlan(subscriptions, plancode) {
  if (window.newBillingConfig.isNewSuccessPage) return false;
  const { type: flowType } = pageConfig;
  const editType = pageConfig?.editConfig?.planType;

  const tollFreePlans = subscriptions.filter((s) => getPlanTypeByCategory(s.category) === planTypes.TOLLFREE);
  const hasUnSupportBillingCycleTollFreePlan = _.some(tollFreePlans, p => isOnlineNotSupportPlan({
    planType: planTypes.TOLLFREE,
    billingCycle: p.billingCycle,
    category: p.category,
  }));
  const hasUnSupportBillingCyclePbxPlan = subscriptions.some((s) =>
    [planTypes.ZOOM_ONE_BUNDLE, planTypes.ZOOM_UNITED, planTypes.ZOOM_PHONE, planTypes.ZONE_PRO_PLUS].includes(getPlanTypeByCategory(s.category)) &&
    ONLINE_NOT_SUPPORT_BILLING_CYCLE.includes(s.billingCycle));

  if (flowType === flowTypes.upsell && [planTypes.ZOOM_ONE_BUNDLE, planTypes.ZOOM_PHONE, planTypes.ZONE_PRO_PLUS].includes(editType)) {
    return hasUnSupportBillingCyclePbxPlan;
  }

  if (flowType === flowTypes.add2cart) {
    //biz plus add2cart
    if (isBizPlusContext() || isProPlusContext() || isPbxContext()) {
      if (hasUnSupportBillingCyclePbxPlan) {
        return true;
      }
    }
    // Special case: Add tollfree unlimited plan in zoom rooms add2cart flow
    const excludePlanTypes = [planTypes.ZOOM_MEETING, planTypes.ZOOM_ROOMS];
    if (!excludePlanTypes.includes(editType)) {
      if (!SMB_SUPPORT_ADD2CARD) {
        return false;
      }
      const basePlanInfo = getBasePlanInfo(subscriptions.map(s => {
        s.type = getPlanTypeByCategory(s.category);
        return s;
      }));
      return ONLINE_NOT_SUPPORT_BILLING_CYCLE.includes(basePlanInfo?.plans[0]?.billingCycle);
    }
  } else if ([flowTypes.biz2bundle, flowTypes.pro2prop, flowTypes.pro2bundle].includes(flowType)) {
    if (hasUnSupportBillingCyclePbxPlan) {
      return true;
    }
  }

  //these may both in edit and add flow
  if (planTypes.ZOOM_MEETING === editType) {
    if (hasUnSupportBillingCycleTollFreePlan || hasUnSupportBillingCyclePbxPlan) {
      return true;
    }
  } else if ([planTypes.ZOOM_PHONE].includes(editType)) {
    if (hasUnSupportBillingCyclePbxPlan) {
      return true
    }
  } else if ([planTypes.TOLLFREE, planTypes.ZOOM_ROOMS].includes(editType)) {
    if (hasUnSupportBillingCycleTollFreePlan) {
      return true
    }
  }

  const currentEditPlan = getCurrentEditPlan(flowType, subscriptions, plancode);
  const currentEditPlanType = getPlanTypeByCategory(currentEditPlan?.category);

  return ONLINE_NOT_SUPPORT_BILLING_CYCLE.includes(currentEditPlan?.billingCycle) || [planTypes.CMR, ...MULTI_UPDATE_PLAN_TYPES].includes(currentEditPlanType);
}

export function getCurrentEditPlan (flowType, subscriptions, plancode) {
  const flowTypeMap = {
    [flowTypes.pro2biz]: () => subscriptions.find(({ parentCategory }) => toUpper(parentCategory) === toUpper(BASE_PLAN_SUB_TYPES.PRO)),
    [flowTypes.pro2prop]: () => subscriptions.find(({ parentCategory }) => toUpper(parentCategory) === toUpper(BASE_PLAN_SUB_TYPES.PRO)),
    [flowTypes.biz2bundle]: () => subscriptions.find(({ parentCategory }) => toUpper(parentCategory) === toUpper(BASE_PLAN_SUB_TYPES.BIZ)),
    [flowTypes.prop2bundle]: () => subscriptions.find(({ parentCategory }) => toUpper(parentCategory) === toUpper(ZONE_PRO_PLUS_PARENT_CATEGORY)),
  };

  return  flowTypeMap[flowType]
    ? flowTypeMap[flowType]()
    : subscriptions.find(({ plan_code }) => plan_code === plancode);
}

function getPricingMapNodes(currentEditPlan, planMap = {}, forRemove = false) {
  const map = {
    [planTypes.ZOOM_MEETING]: () => {
      if (pageConfig.type === flowTypes.pro2biz) {
        return [planMap.pro, planMap.biz];
      } else if ([BASE_PLAN_SUB_TYPES.PRO, BASE_PLAN_SUB_TYPES.BIZ, NEW_PRO_CATEGORY].includes(currentEditPlan?.category)) {
        return compact([planMap.pro, planMap.biz, planMap.zoomone[ZoomOneProPlusKey], forRemove ? null : planMap.tollfreeUnlimited]);
      } else if (BASE_PLAN_SUB_TYPES.EDUCATION === currentEditPlan?.category) {
        return [planMap.education];
      } else if (!currentEditPlan && pageConfig.type === flowTypes.add2cart) {
        return [planMap.pro, planMap.biz];
      }
      return [planMap[currentEditPlan?.parentCategory?.toLowerCase()]]
    },
    [planTypes.ZONE_PRO_PLUS]: () => {
      if(pageConfig.type === flowTypes.pro2prop){
        return [planMap.pro, planMap.zoomone[ZoomOneProPlusKey]];
      }
      return [planMap.zoomone[ZoomOneProPlusKey]];
    },
    [planTypes.LARGE_MEETING]: () => {
      const keys = Object.keys(planMap).filter((k) => k.match(/large(\d+)/));
      return keys.map(k => planMap[k]);
    },
    [planTypes.WEBINAR]: () => {
      const keys = Object.keys(planMap).filter((k) => k.match(/webinar(\d+)/));
      return keys.map(k => planMap[k]);
    },
    [planTypes.EVENT_SAAS]: () => {
      const keys = Object.keys(planMap).filter((k) => k.match(/eventsaas(\d+)/));
      return keys.map(k => planMap[k]);
    },
    [planTypes.EVENT_SAAS_CORE]: () => {
      const keys = Object.keys(planMap).filter((k) => k.match(/eventsaascore(\d+)/));
      return keys.map(k => planMap[k]);
    },
    [EVENT_SAAS_ADDITIONAL_HOST]: EVENT_SAAS_ADDITIONAL_HOST,
    [EVENT_SAAS_CORE_ADDITIONAL_HOST]: EVENT_SAAS_CORE_ADDITIONAL_HOST,
    [EVENT_SAAS_ADDITIONAL_CO_EDITOR]: EVENT_SAAS_ADDITIONAL_CO_EDITOR,
    [EVENT_SAAS_CORE_ADDITIONAL_CO_EDITOR]: EVENT_SAAS_CORE_ADDITIONAL_CO_EDITOR,
    [planTypes.CMR]: () => {
      const keys = Object.keys(planMap).filter((k) => Object.keys(cmrCapacityMap).includes(k));
      return keys.map(k => planMap[k]);
    },
    [planTypes.ZCM_STORAGE]: () => {
      const keys = Object.keys(planMap).filter((k) => Object.keys(zcmStorageCapacityMap).includes(k));
      return keys.map(k => planMap[k]);
    },
    [planTypes.ZOOM_WHITEBOARD]: () => [planMap.whiteboard_plus, planMap.whiteboard],
    [planTypes.ZOOM_SCHEDULER]: 'zsched_price_list',
    [planTypes.TRANSLATION_CAPTIONS]: 'translation_captions',
    [planTypes.ZOOM_ROOMS]: () => [planMap.zr, forRemove ? null : planMap.tollfreeUnlimited],
    [planTypes.ROOM_CONNECTOR]: 'rc',
    [planTypes.ZOOM_CLIPS]: 'zoom_clips',
    [planTypes.ZOOM_DOCS]: 'zoom_docs',
    [planTypes.ZOOM_REVENUE_ACCELERATOR]: 'zkw_price_list',
    [planTypes.ZCM]: 'zcm',
    [planTypes.CONCURRENCY_MEETING_BASIC]: 'cmb_price_list',
    [planTypes.CONCURRENT_MEETING_PLUS]: 'cmp_price_list',
    [planTypes.VISITOR_MANAGEMENT]: 'zvstmgt',
    [planTypes.CUSTOM_AVATARS]: 'custom_avatars',
    [planTypes.WORKSPACE_RESERVATION]: 'workspace_reservation',
    [planTypes.TOLLFREE]: () => [planMap.tollfree, planMap.tollfreeUnlimited],
    [planTypes.ZOOM_ONE_BUNDLE]: () => {
      const isBizPlusContext = pageConfig?.editConfig?.planType === planTypes.ZOOM_ONE_BUNDLE
      if (isBizPlusContext) {
        return [planMap.biz]
      }
      return []
    }
  };
  let planType = pageConfig?.editConfig?.planType ?? getPlanTypeByCategory(currentEditPlan?.category);
  if ([planTypes.ADDITIONAL_HOST, planTypes.ADDITIONAL_CO_EDITOR].includes(planType)) {
    planType = pageConfig?.editConfig?.category || currentEditPlan?.category;
  }

  console.assert(planType, 'mismatch plan type', currentEditPlan);
  const matchedInitor = map[planType] ?? planType
  console.assert(matchedInitor, 'mismatch initor', planType);
  return isFunction(matchedInitor) ? compact(matchedInitor()) : compact([planMap[matchedInitor]]);
}

// Merge "zoomone.smbInfo.zoom-one-biz-plus" to "zoomone.zoom-one-biz-plus"
// Merge "zoomone.smbInfo.zoom-one-pro-plus" to "zoomone.zoom-one-pro-plus"
function flatSMBBundlePlan(planMap, bundleKey, filterBillingCycles) {
  const bundlePlans = get(planMap, `zoomone.${bundleKey}`, []);
  let smbPlans = get(planMap, `zoomone.smbInfo.${bundleKey}`, []);
  if (smbPlans.length > 0) {
    if(filterBillingCycles?.length > 0) {
      smbPlans = smbPlans.filter(smbBundlePlan => filterBillingCycles.includes(smbBundlePlan.billing_cycle));
    }
    smbPlans.forEach(smbBundlePlan => {
      if (!bundlePlans.find(bundlePlan => bundlePlan.plan_code === smbBundlePlan.plan_code)) {
        bundlePlans.push(smbBundlePlan);
      }
    });
  }
}

function formatPlanMap(subscriptions, planMap, currentPlanBillingCycle) {
  //handle bundle plan
  ["zoom-one-biz-plus", ZoomOneProPlusKey].forEach(key => {
    const pbxBillingCycles = subscriptions.filter(s => getPlanTypeByCategory(s.category) === planTypes.ZOOM_PHONE).map(s => BillingCycleEnum[s.billingCycle]);
    flatSMBBundlePlan(planMap, key, key === ZoomOneProPlusKey ? [BILLING_CYCLE.MONTHLY, BILLING_CYCLE.ANNUAL, currentPlanBillingCycle, ...pbxBillingCycles] : []);
  });
  //handle zoom revenue accelerator, zcm
  const isSpecialCycle = ONLINE_NOT_SUPPORT_BILLING_CYCLE.includes(currentPlanBillingCycle);
  const nodeKeys = ["zkw_price_list", "cmb_price_list", "cmp_price_list", "zvstmgt", ...(isSpecialCycle ? ["zcm"] : [])];
  nodeKeys.forEach((nodeKey) => {
    if (planMap[nodeKey]) {
      let nodeMap = planMap[nodeKey];
      const smbNodes = nodeMap.smbInfo ?? {};
      nodeMap = { ...nodeMap, ...smbNodes, isObjMap: true };
      //set billing_cycle
      Object.keys(nodeMap).forEach((node) => {
        try {
          let targetBillingCycle = Object.values(BILLING_CYCLE).find((billingCycle) => node.includes(billingCycle));
          if (!targetBillingCycle) {
            // like zcm 'archiving_compliance_yearly', 'archiving_compliance_two_year', 'archiving_compliance_three_year',
            if (node.includes('_yearly')) {
              targetBillingCycle = BILLING_CYCLE.ANNUAL;
            } else if (node.includes('_two_year')) {
              targetBillingCycle = BILLING_CYCLE.TWO_YEARS;
            } else if (node.includes('_three_year')) {
              targetBillingCycle = BILLING_CYCLE.THREE_YEARS;
            }
          }

          if (targetBillingCycle) {
            nodeMap[node].billing_cycle = targetBillingCycle;
          }
        } catch(e) {
          console.error(e)
        }
      });
      planMap[nodeKey] = nodeMap;
    }
  });

}

/**
 * @param {object} planMap
 * @param {string} currentPlanBillingCycle
 * @param {object} currentEditPlan
 * @param {string<flowTypes>} flowType
 * @description Add quarterly_plancode/quarterly_amount to pricing map
 * @returns {void}
 */
function addSMBCycleInfoPlanNode(planMap, currentPlanBillingCycle, currentEditPlan, subscriptions) {
  const shouldAddSmbCyclePlans = getPricingMapNodes(currentEditPlan, planMap);

  formatPlanMap(subscriptions, planMap, currentPlanBillingCycle);

  //handle pro increase price
  let specialProConfigs = NEW_PRO_SPECIAL_CYCLES.filter(c =>
    subscriptions.some(s => c.code === s.plan_code));
  //biz -> pro
  if(BASE_PLAN_SUB_TYPES.BIZ === currentEditPlan?.category) {
    specialProConfigs = NEW_PRO_SPECIAL_CYCLES
  }
  specialProConfigs.forEach((c) => {
    Object.assign(planMap.pro.smbInfo, {
      [c.oPrefix + 'plancode']: planMap.pro?.smbInfo?.[c.prefix + 'plancode'],
      [c.oPrefix + 'amount']: planMap.pro?.smbInfo?.[c.prefix + 'amount'],
      [c.oPrefix + 'plantype']: planMap.pro?.smbInfo?.[c.prefix + 'plantype'],
    });
  });

  // pro, biz, bizplus optional billing cycle should be consistent
  const currentEditPlanType = getPlanTypeByCategory(currentEditPlan?.category)
  const { type: flowType, from_sub: fromSub} = pageConfig;

  if (MULTI_UPDATE_PLAN_TYPES.includes(currentEditPlanType)) {
    // Add pro, biz for plans which support auto-add
    Object.assign(planMap.pro, { addAllCycle: true })
    Object.assign(planMap.biz, { addAllCycle: true })
    shouldAddSmbCyclePlans.push(planMap.pro, planMap.biz)
  } else if ([planTypes.TOLLFREE, planTypes.ZOOM_ROOMS, planTypes.ZOOM_MEETING].includes(currentEditPlanType) || (flowType === flowTypes.add2cart && getPlanTypeByCategory(fromSub) === planTypes.ZOOM_ROOMS)) {
    // case1: Edit plan is Toll free/ZR/Zoom Meeting
    // case2: Zoom Rooms add2cart flow
    if (planMap.tollfreeUnlimited)
      Object.assign(planMap.tollfreeUnlimited, { addAllCycle: true })
  }

  isDebug && console.log('smb:shouldAddSmbCyclePlans:', shouldAddSmbCyclePlans);
  compact(shouldAddSmbCyclePlans).forEach((plan) => {
    const { smbInfo } = plan;
    if (!smbInfo || !isObject(smbInfo)) {
      return;
    }

    let planHandlerList = [
      {
        prefix: 'quarterly',
        billingCycle: BILLING_CYCLE.QUARTERLY,
      },
      {
        prefix: 'two_years',
        fieldName: BILLING_CYCLE_PREFIX[BILLING_CYCLE.TWO_YEARS],
        billingCycle: BILLING_CYCLE.TWO_YEARS,
      },
      {
        prefix: 'three_years',
        fieldName: BILLING_CYCLE_PREFIX[BILLING_CYCLE.THREE_YEARS],
        billingCycle: BILLING_CYCLE.THREE_YEARS,
      },
    ];

    if (planTypes.CMR === currentEditPlanType || plan.category === AUDIO_SUB_TYPES.TOLL_FREE) {
      planHandlerList.push({
        prefix: 'yearly',
        billingCycle: BILLING_CYCLE.ANNUAL,
      });
    }

    // some plan node may not be a flat object, like: zsched_price_list
    const flatSMBObj = (obj, resultObj = {}, prefix = '') => {
      return Object.keys(obj).reduce((result, field) => {
        const value = obj[field];
        const fieldName = compact([prefix, field]).join('_');
        if (isObject(value) && !isArray(value)) {
          result = flatSMBObj(value, result, fieldName);
        } else {
          result[fieldName] = value;
        }
        return result;
      }, resultObj);
    };
    const smbInfoObj = flatSMBObj(smbInfo);
    planHandlerList.forEach((item) => {
      const { prefix, fieldName, billingCycle } = item;
      const smbInfoObjKeys = Object.keys(smbInfoObj);
      const planCodeFieldName = smbInfoObjKeys.find((key) => new RegExp(`^(?!old_).*${prefix}.*plan.*code`).test(key)),
        planAmountFieldName = smbInfoObjKeys.find((key) => new RegExp(`^(?!old_).*${prefix}.*(amount|price|prices)$`).test(key)),
        planTypeFieldName = smbInfoObjKeys.find((key) => new RegExp(`^(?!old_).*${prefix}.*plan.*type`).test(key)),
        oldPlanCodeFieldName = smbInfoObjKeys.find((key) => new RegExp(`old_${prefix}.*plan.*code`).test(key));
      const planCode = get(smbInfoObj, planCodeFieldName, null),
        planAmount = get(smbInfoObj, planAmountFieldName, null),
        planType = get(smbInfoObj, planTypeFieldName, null),
        oldPlanCode = get(smbInfoObj, oldPlanCodeFieldName, null);

      // Support auto-add non-standard billing cycle pro or biz
      const canAddBillingCycle = plan.addAllCycle || billingCycle === currentPlanBillingCycle || pageConfig?.type === flowTypes.add2cart

      if (isString(planCode) && planAmount && canAddBillingCycle) {
        const newPlanCodeFieldName = [fieldName || prefix, PRICING_INFO_FIELD_SUFFIX.PLAN_CODE].join('_');
        const newPlanAmountFieldName = [fieldName || prefix, PRICING_INFO_FIELD_SUFFIX.AMOUNT].join('_');
        const newPlanTypeFieldName = [fieldName || prefix, PRICING_INFO_FIELD_SUFFIX.PLAN_TYPE].join('_');
        const newOldPlanCodeFieldName = 'old_plan_code';
        plan[newPlanCodeFieldName] = planCode;
        plan[newPlanAmountFieldName] = planAmount;
        plan[newPlanTypeFieldName] = planType;
        plan[newOldPlanCodeFieldName] = oldPlanCode;
      }
    });
  })
}
/**
 * @param {object} planMap
 * @param {string} currentPlanBillingCycle
 * @param {object} currentEditPlan
 * @param {string<flowTypes>} flowType
 * @description Hide cycles which are not allowed to switch during SMB edit flow
 * @returns {void}
 */
function removeDisabledBillingCycles(planMap, currentPlanBillingCycle, currentEditPlan, flowType, subscriptions) {
  const currentPlanType = pageConfig?.editConfig?.planType;

  function getDisabledCycles() {
    if (flowType === flowTypes.add2cart) {
      //now support add pro plus / biz plus
      if(isProPlusContext()){
        const pbxBillingCycles = subscriptions.filter(s => getPlanTypeByCategory(s.category) === planTypes.ZOOM_PHONE).map(s => BillingCycleEnum[s.billingCycle]);
        return keys(SMB_BILLING_CYCLE_MAP).filter(
          (billingCycle) => ![BILLING_CYCLE.ANNUAL, currentPlanBillingCycle, ...pbxBillingCycles].includes(billingCycle),
        );
      }

      //add2card selectable billing cycles rely on base plan billing cycle
      const phase1SupportAdd2Card = [planTypes.WEBINAR, planTypes.LARGE_MEETING, planTypes.TRANSLATION_CAPTIONS, planTypes.ROOM_CONNECTOR, planTypes.TOLLFREE, planTypes.EVENT_SAAS, planTypes.EVENT_SAAS_CORE];
      if (phase1SupportAdd2Card.includes(currentPlanType)) {
        const basePlanInfo = getBasePlanInfo(subscriptions.map(s => {
          s.type = getPlanTypeByCategory(s.category);
          return s;
        }));
        switch (basePlanInfo?.plans[0]?.billingCycle) {
          case BillingCycleEnum[BILLING_CYCLE.MONTHLY] :
          case BillingCycleEnum[BILLING_CYCLE.ANNUAL] :
            return [BILLING_CYCLE.QUARTERLY, BILLING_CYCLE.TWO_YEARS, BILLING_CYCLE.THREE_YEARS];
          case BillingCycleEnum[BILLING_CYCLE.QUARTERLY] :
            return [BILLING_CYCLE.ANNUAL, BILLING_CYCLE.TWO_YEARS, BILLING_CYCLE.THREE_YEARS];
          case BillingCycleEnum[BILLING_CYCLE.TWO_YEARS] :
            return [BILLING_CYCLE.ONE_MONTH, BILLING_CYCLE.QUARTERLY, BILLING_CYCLE.THREE_YEARS];
          case BillingCycleEnum[BILLING_CYCLE.THREE_YEARS] :
            return [BILLING_CYCLE.ONE_MONTH, BILLING_CYCLE.QUARTERLY, BILLING_CYCLE.TWO_YEARS];
        }
      } else {
        return [BILLING_CYCLE.QUARTERLY, BILLING_CYCLE.TWO_YEARS, BILLING_CYCLE.THREE_YEARS];
      }
    } else if (flowType === flowTypes.pro2biz) {
      // Pro2Biz flow or Edit Biz plus flow
      // options: Current Billing Cycle, Annual
      return keys(SMB_BILLING_CYCLE_MAP).filter(
        (billingCycle) => ![BILLING_CYCLE.ANNUAL, currentPlanBillingCycle].includes(billingCycle),
      );
    } else if(flowType === flowTypes.pro2prop) {
      const pbxBillingCycles = subscriptions.filter(s => getPlanTypeByCategory(s.category) === planTypes.ZOOM_PHONE).map(s => BillingCycleEnum[s.billingCycle]);
      return keys(SMB_BILLING_CYCLE_MAP).filter(
        (billingCycle) => ![BILLING_CYCLE.ANNUAL, currentPlanBillingCycle, ...pbxBillingCycles].includes(billingCycle),
      );
    } else {
      // Edit flow
      const isBasePlan = SMB_MEETING_BASE_PLAN.includes(getPlanTypeFromParentCategory(currentEditPlan?.parentCategory));
      return keys(SMB_BILLING_CYCLE_MAP).filter((billingCycle) => {
        if (isBasePlan) {
          const selectableBillingCycle = [currentPlanBillingCycle, BILLING_CYCLE.ANNUAL]
          return !selectableBillingCycle.includes(billingCycle)
        } else if (currentPlanType === planTypes.TOLLFREE) {
          // Audio plan not disable any billing cycle.The hide logic will handle in the config component
          return false
        } else {
          // standalone plan allow to switch billing cycle current or annual; other add-ons not allow
          let selectableBillingCycle = STAND_ALONE_PLAN_TYPES.includes(currentPlanType) ? [currentPlanBillingCycle, BILLING_CYCLE.ANNUAL] : [currentPlanBillingCycle];

          //cmb, cmp normal billing cycle may also enter here
          if (SMB_ALWAYS_FETCH_NONSTANDARD_TYPES.includes(currentPlanType) && [BILLING_CYCLE.MONTHLY, BILLING_CYCLE.ANNUAL].includes(currentPlanBillingCycle)) {
            selectableBillingCycle = [BILLING_CYCLE.MONTHLY, BILLING_CYCLE.ANNUAL];
          }
          return !selectableBillingCycle.includes(billingCycle);
        }
      });
    }
  }
  let shouldHideCyclePlans = []
  if (pageConfig.type === flowTypes.add2cart) {
    shouldHideCyclePlans = getPricingMapNodes(null, planMap, true);
  } else {
    shouldHideCyclePlans = ONLINE_NOT_SUPPORT_BILLING_CYCLE.includes(currentEditPlan?.billingCycle) ? getPricingMapNodes(currentEditPlan, planMap, true) : [];
    if (planTypes.CMR === currentPlanType && BillingCycleEnum[BILLING_CYCLE.ANNUAL] === currentEditPlan?.billingCycle) {
      shouldHideCyclePlans = getPricingMapNodes(currentEditPlan, planMap, true);
    } else if(SMB_ALWAYS_FETCH_NONSTANDARD_TYPES.includes(currentPlanType)) {
      shouldHideCyclePlans = getPricingMapNodes(currentEditPlan, planMap, true);
    }
  }
  if (currentPlanType === planTypes.ZOOM_ROOMS) {
    // Don't hide tollfree unlimted plan cycle in the Zoom Rooms add2cart flow
    shouldHideCyclePlans = shouldHideCyclePlans.filter(p => p.category !== 'tollfreeUnlimited');
  }
  const disableCycles = getDisabledCycles();
  isDebug && console.log('smb:disableCycles:', disableCycles);
  isDebug && console.log('smb:shouldHideCyclePlans:', shouldHideCyclePlans);
  disableCycles.forEach((billingCycle) => {
    const curBillingCycleItem = SMB_BILLING_CYCLE_MAP[billingCycle];
    const { prefix } = curBillingCycleItem;
    if (prefix) {
      shouldHideCyclePlans.forEach((planOrPlans) => {
        //zoomone's data is array, like zoomone.zoom-one-pro-plus
        if (isArray(planOrPlans)) {
          planOrPlans = _.remove(planOrPlans, (p) => p.billing_cycle === billingCycle);
        } else if (planOrPlans?.isObjMap) {
          //handle like zkw_price_list
          const delKey = Object.keys(planOrPlans).filter((k) => planOrPlans[k]?.billing_cycle === billingCycle);
          delKey && delete planOrPlans[delKey];
        } else {
          planOrPlans[prefix + '_hide'] = true;
        }
      })
    }
  });
}

export function loadNonStandardBillingCyclePlanData(planMap, currentEditPlan, flowType, subscriptions) {
  const currentPlanBillingCycle = convertBillingCycleEnumToString(currentEditPlan?.billingCycle);
  // add unsupport billing cycle pricing info
  addSMBCycleInfoPlanNode(planMap, currentPlanBillingCycle, currentEditPlan, subscriptions);

  // Disable the billing cycles which is not allowed to select
  // Per latest design, we do not disable but hide the unselectable billing cycle
  removeDisabledBillingCycles(planMap, currentPlanBillingCycle, currentEditPlan, flowType, subscriptions);

  return Promise.resolve(planMap);
}
